import { z } from "zod";
import { actorKindSchema, restaurantStatusSchema } from "./enums";
import { downloadLinkSchema } from "./files";
import { paiseSchema } from "./money";
import { gstinSchema } from "./restaurant";

/**
 * What restaurants pay LuckyMenu. See docs/architecture/platform-billing.md.
 * The Postgres enums in packages/db are built from these arrays.
 */

export const PLAN_INTERVALS = ["monthly", "yearly"] as const;
export const planIntervalSchema = z.enum(PLAN_INTERVALS);
export type PlanInterval = z.infer<typeof planIntervalSchema>;

export const SUBSCRIPTION_STATUSES = [
	"trialing",
	"active",
	"past_due",
	"suspended",
	"cancelled",
] as const;
export const subscriptionStatusSchema = z.enum(SUBSCRIPTION_STATUSES);
export type SubscriptionStatus = z.infer<typeof subscriptionStatusSchema>;

export const PLATFORM_INVOICE_STATUSES = ["draft", "issued", "paid", "void"] as const;
export const platformInvoiceStatusSchema = z.enum(PLATFORM_INVOICE_STATUSES);
export type PlatformInvoiceStatus = z.infer<typeof platformInvoiceStatusSchema>;

/** `razorpay` is recorded by the payment link webhook later, never typed by an admin. */
export const PLATFORM_PAYMENT_METHODS = ["upi", "cash", "bank", "razorpay"] as const;
export const platformPaymentMethodSchema = z.enum(PLATFORM_PAYMENT_METHODS);
export type PlatformPaymentMethod = z.infer<typeof platformPaymentMethodSchema>;

/** Feature flags a plan includes. */
export const PLAN_FEATURES = [
	"qr_ordering",
	"billing",
	"printing",
	"website",
	"offers",
	"reports",
] as const;
export const planFeatureSchema = z.enum(PLAN_FEATURES);
export type PlanFeature = z.infer<typeof planFeatureSchema>;

export const PLAN_LIMITS = ["max_staff", "max_items", "max_tables", "storage_mb"] as const;
export type PlanLimit = (typeof PLAN_LIMITS)[number];

/** Numeric limits of a plan. A missing limit means no limit. */
export const planLimitsSchema = z
	.object({
		max_staff: z.number().int().positive(),
		max_items: z.number().int().positive(),
		max_tables: z.number().int().positive(),
		storage_mb: z.number().int().positive(),
	})
	.partial();
export type PlanLimits = z.infer<typeof planLimitsSchema>;

/**
 * What a restaurant may use: the features and limits of its subscription's
 * plan. The API reads them through `getEntitlements`, cached for 60 seconds.
 */
export const entitlementsSchema = z.object({
	planCode: z.string(),
	status: subscriptionStatusSchema,
	features: z.array(planFeatureSchema),
	limits: planLimitsSchema,
});
export type Entitlements = z.infer<typeof entitlementsSchema>;

/** Codes of the placeholder plans that the first migration creates (open question Q5). */
export const PLACEHOLDER_PLAN_CODES = ["trial", "menu", "standard", "custom"] as const;

/** The plan a new restaurant's subscription starts on. */
export const TRIAL_PLAN_CODE = "trial";

export const DEFAULT_TRIAL_DAYS = 14;
export const DEFAULT_GRACE_DAYS = 7;
/** A cancelled restaurant is archived this many days after cancellation. */
export const ARCHIVE_AFTER_CANCEL_DAYS = 90;

/** A plan as the admin app shows it. */
export const planSchema = z.object({
	id: z.uuid(),
	code: z.string(),
	name: z.string(),
	pricePaise: paiseSchema,
	interval: planIntervalSchema,
	features: z.array(planFeatureSchema),
	limits: planLimitsSchema,
	isActive: z.boolean(),
	sortOrder: z.number().int(),
});
export type Plan = z.infer<typeof planSchema>;

/**
 * `public.platformBilling.getProductInfo`: what LuckyMenu's product site
 * shows (P12-T20). The plans on offer (active, without `trial`, which is the
 * free start of every plan), the trial's length and the WhatsApp number to
 * reach us. A price of 0 is a placeholder (open question Q5): the site says
 * the price is to be announced.
 */
export const productInfoSchema = z.object({
	plans: z.array(
		planSchema.pick({
			code: true,
			name: true,
			pricePaise: true,
			interval: true,
			features: true,
			limits: true,
		}),
	),
	trialDays: z.number().int(),
	whatsapp: z.string().nullable(),
});
export type ProductInfo = z.infer<typeof productInfoSchema>;

export const PLATFORM_TAX_KINDS = ["none", "intra_state", "inter_state"] as const;

/**
 * Everything printed on an invoice, copied when it is issued. Editing a plan,
 * the settings or the restaurant never changes an issued invoice.
 */
export const platformInvoiceSnapshotSchema = z.object({
	invoiceNo: z.string(),
	issuedAt: z.iso.datetime(),
	dueDate: z.iso.date(),
	periodStart: z.iso.datetime(),
	periodEnd: z.iso.datetime(),
	seller: z.object({
		legalName: z.string(),
		address: z.string().nullable(),
		gstin: z.string().nullable(),
		upiId: z.string().nullable(),
	}),
	buyer: z.object({
		restaurantId: z.uuid(),
		name: z.string(),
		legalName: z.string().nullable(),
		addressLine1: z.string().nullable(),
		addressLine2: z.string().nullable(),
		city: z.string().nullable(),
		stateCode: z.string().nullable(),
		pincode: z.string().nullable(),
	}),
	plan: z.object({
		code: z.string(),
		name: z.string(),
		interval: planIntervalSchema,
		pricePaise: paiseSchema,
	}),
	/** Set when the restaurant's custom price replaced the plan price. */
	customPricePaise: paiseSchema.nullable(),
	tax: z.object({
		kind: z.enum(PLATFORM_TAX_KINDS),
		rateBp: z.number().int().nonnegative(),
		cgstPaise: paiseSchema,
		sgstPaise: paiseSchema,
		igstPaise: paiseSchema,
	}),
	amountPaise: paiseSchema,
	gstPaise: paiseSchema,
	totalPaise: paiseSchema,
});
export type PlatformInvoiceSnapshot = z.infer<typeof platformInvoiceSnapshotSchema>;

export const platformInvoiceSchema = z.object({
	id: z.uuid(),
	restaurantId: z.uuid(),
	invoiceNo: z.string(),
	status: platformInvoiceStatusSchema,
	periodStart: z.iso.datetime(),
	periodEnd: z.iso.datetime(),
	dueDate: z.iso.date(),
	amountPaise: paiseSchema,
	gstPaise: paiseSchema,
	totalPaise: paiseSchema,
	/** The sum of the payments that are not reversed. */
	paidPaise: paiseSchema,
	issuedAt: z.iso.datetime().nullable(),
	paidAt: z.iso.datetime().nullable(),
	voidedAt: z.iso.datetime().nullable(),
	voidReason: z.string().nullable(),
	snapshot: platformInvoiceSnapshotSchema,
});
export type PlatformInvoice = z.infer<typeof platformInvoiceSchema>;

export const subscriptionSchema = z.object({
	id: z.uuid(),
	restaurantId: z.uuid(),
	status: subscriptionStatusSchema,
	plan: planSchema,
	customPricePaise: paiseSchema.nullable(),
	trialEndsAt: z.iso.datetime().nullable(),
	currentPeriodStart: z.iso.datetime().nullable(),
	currentPeriodEnd: z.iso.datetime().nullable(),
	graceUntil: z.iso.datetime().nullable(),
	cancelledAt: z.iso.datetime().nullable(),
	notes: z.string().nullable(),
});
export type Subscription = z.infer<typeof subscriptionSchema>;

export const platformPaymentSchema = z.object({
	id: z.uuid(),
	restaurantId: z.uuid(),
	invoiceId: z.uuid(),
	method: platformPaymentMethodSchema,
	amountPaise: paiseSchema,
	reference: z.string().nullable(),
	receivedAt: z.iso.datetime(),
	note: z.string().nullable(),
	reversedAt: z.iso.datetime().nullable(),
	reverseReason: z.string().nullable(),
});
export type PlatformPayment = z.infer<typeof platformPaymentSchema>;

/** `admin.platformBilling.getSubscription`: the restaurant's subscription tab. */
export const subscriptionDetailSchema = z.object({
	subscription: subscriptionSchema,
	/** Newest period first. */
	invoices: z.array(platformInvoiceSchema),
	/** Newest first, reversed ones included. */
	payments: z.array(platformPaymentSchema),
});
export type SubscriptionDetail = z.infer<typeof subscriptionDetailSchema>;

/**
 * `staff.platformBilling.getMySubscription`, for the owner: the subscription
 * without our internal notes, the invoices that are not void (newest period
 * first), and whom to pay. Works while the restaurant is suspended.
 */
export const mySubscriptionSchema = z.object({
	subscription: subscriptionSchema.omit({ notes: true }),
	invoices: z.array(platformInvoiceSchema),
	/** Whom to pay, and the WhatsApp number to send the payment's reference to. */
	payTo: z.object({
		legalName: z.string(),
		upiId: z.string().nullable(),
		whatsapp: z.string().nullable(),
	}),
});
export type MySubscription = z.infer<typeof mySubscriptionSchema>;

/**
 * `admin.platformBilling.getHistory`: the restaurant's billing audit entries,
 * newest first: subscription changes and overrides, invoices, payments, and
 * status changes made for the subscription.
 */
export const billingHistoryEntrySchema = z.object({
	id: z.uuid(),
	action: z.string(),
	actorKind: actorKindSchema,
	/** The admin's name or email; null for the system. */
	actorName: z.string().nullable(),
	data: z.record(z.string(), z.unknown()),
	createdAt: z.iso.datetime(),
});
export type BillingHistoryEntry = z.infer<typeof billingHistoryEntrySchema>;

/** One line of the due list: an unpaid invoice with its restaurant. */
export const dueInvoiceSchema = z.object({
	invoice: platformInvoiceSchema,
	/** What is still owed. */
	owedPaise: paiseSchema,
	restaurant: z.object({
		id: z.uuid(),
		name: z.string(),
		slug: z.string(),
		phone: z.string().nullable(),
		status: restaurantStatusSchema,
	}),
	subscriptionStatus: subscriptionStatusSchema,
});
export type DueInvoiceLine = z.infer<typeof dueInvoiceSchema>;

/**
 * `admin.platformBilling.getDashboard`. Dates are India's: "this week" is
 * today and the six days after it, "overdue" is due before today, "this
 * month" the calendar month. MRR is what active subscriptions pay a month
 * (a yearly price divided by 12, to the paisa).
 */
export const billingDashboardSchema = z.object({
	today: z.iso.date(),
	dueThisWeek: z.object({ count: z.number().int(), owedPaise: paiseSchema }),
	overdue: z.object({ count: z.number().int(), owedPaise: paiseSchema }),
	suspended: z.number().int(),
	collectedThisMonthPaise: paiseSchema,
	mrrPaise: paiseSchema,
});
export type BillingDashboard = z.infer<typeof billingDashboardSchema>;

/** A reason or note typed by a superadmin, written to the audit log. */
export const adminNoteSchema = z.string().trim().min(5).max(500);

/**
 * `admin.platformBilling.createInvoice`. `invoiceId` is made by the client
 * (`uuidv7`) so a retry returns the invoice instead of issuing a second one.
 * Without `periodStart` the invoice follows the last invoiced or paid period.
 */
export const createPlatformInvoiceInputSchema = z.object({
	restaurantId: z.uuid(),
	invoiceId: z.uuid().optional(),
	periodStart: z.iso.datetime().optional(),
});

/** An invoice, named by its restaurant and ID. */
export const platformInvoiceRefSchema = z.object({
	restaurantId: z.uuid(),
	invoiceId: z.uuid(),
});

/** `getInvoicePdf`: a link to the invoice's PDF for five minutes, and the name to save it as. */
export const invoicePdfLinkSchema = downloadLinkSchema.extend({ fileName: z.string() });
export type InvoicePdfLink = z.infer<typeof invoicePdfLinkSchema>;

/** Only an invoice with no payments can be voided. Its number is never reused. */
export const voidPlatformInvoiceInputSchema = z.object({
	restaurantId: z.uuid(),
	invoiceId: z.uuid(),
	reason: adminNoteSchema,
});

/** Methods an admin records by hand. Razorpay payments arrive by webhook later. */
export const MANUAL_PAYMENT_METHODS = ["upi", "cash", "bank"] as const;

/** A UPI or bank reference as typed. Spaces are dropped and letters upper-cased. */
export const paymentReferenceSchema = z
	.string()
	.transform((v) => v.replace(/\s+/g, "").toUpperCase())
	.pipe(z.string().min(4).max(64));

/**
 * `admin.platformBilling.recordPayment`. `paymentId` is made by the client
 * (`uuidv7`), so a retry records nothing twice. UPI and bank payments need
 * their reference; each reference is accepted once per method.
 */
export const recordPlatformPaymentInputSchema = z
	.object({
		restaurantId: z.uuid(),
		invoiceId: z.uuid(),
		paymentId: z.uuid(),
		method: z.enum(MANUAL_PAYMENT_METHODS),
		amountPaise: paiseSchema.positive(),
		reference: paymentReferenceSchema.nullable().optional(),
		receivedAt: z.iso.datetime(),
		note: z.string().trim().max(500).optional(),
	})
	.refine((p) => p.method === "cash" || Boolean(p.reference), {
		path: ["reference"],
		message: "UPI and bank payments need their reference",
	});
export type RecordPlatformPaymentInput = z.infer<typeof recordPlatformPaymentInputSchema>;

/** Reverses a payment recorded by mistake. The invoice is unpaid again. */
export const reversePlatformPaymentInputSchema = z.object({
	restaurantId: z.uuid(),
	paymentId: z.uuid(),
	reason: adminNoteSchema,
});

export const platformPaymentResultSchema = z.object({
	payment: platformPaymentSchema,
	invoice: platformInvoiceSchema,
	subscription: subscriptionSchema,
});
export type PlatformPaymentResult = z.infer<typeof platformPaymentResultSchema>;

/** Superadmin overrides of a subscription. Dates must be in the future. */
export const SUBSCRIPTION_CHANGE_KINDS = [
	"extendTrial",
	"extendGrace",
	"reactivate",
	"changePlan",
	"setCustomPrice",
] as const;
export type SubscriptionChangeKind = (typeof SUBSCRIPTION_CHANGE_KINDS)[number];

export const subscriptionChangeSchema = z.discriminatedUnion("kind", [
	/** A running trial, or one that ended unpaid, runs until the date. */
	z.object({ kind: z.literal("extendTrial"), until: z.iso.datetime() }),
	/** A past due or suspended subscription is past due until the date. */
	z.object({ kind: z.literal("extendGrace"), until: z.iso.datetime() }),
	/** A lapsed subscription is active until the date, without payment. */
	z.object({ kind: z.literal("reactivate"), until: z.iso.datetime() }),
	/** Takes effect from the next invoice. */
	z.object({ kind: z.literal("changePlan"), planId: z.uuid() }),
	/** Replaces the plan price on the next invoices; null goes back to the plan price. */
	z.object({ kind: z.literal("setCustomPrice"), customPricePaise: paiseSchema.nullable() }),
]);
export type SubscriptionChange = z.infer<typeof subscriptionChangeSchema>;

/** `admin.platformBilling.updateSubscription`. The note is required and audited. */
export const updateSubscriptionInputSchema = z.object({
	restaurantId: z.uuid(),
	change: subscriptionChangeSchema,
	note: adminNoteSchema,
});
export type UpdateSubscriptionInput = z.infer<typeof updateSubscriptionInputSchema>;

/** Lower case letters, digits and underscores, starting with a letter. Never changes. */
export const planCodeSchema = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[a-z][a-z0-9_]{1,29}$/, "2 to 30 lowercase letters, digits or underscores");

/**
 * `admin.platformBilling.upsertPlan`. The client makes `id` (`uuidv7`) for a
 * new plan, so a retry updates the plan it created. The code of an existing
 * plan cannot change.
 */
export const upsertPlanInputSchema = z.object({
	id: z.uuid(),
	code: planCodeSchema,
	name: z.string().trim().min(1).max(60),
	pricePaise: paiseSchema,
	interval: planIntervalSchema,
	features: z.array(planFeatureSchema).transform((f) => [...new Set(f)]),
	limits: planLimitsSchema,
	isActive: z.boolean(),
	sortOrder: z.number().int().min(0).max(1000),
});
export type UpsertPlanInput = z.infer<typeof upsertPlanInputSchema>;

/** A UPI ID (VPA) such as `luckymenu@okaxis`. Stored in lower case. */
export const upiIdSchema = z
	.string()
	.trim()
	.toLowerCase()
	.regex(/^[a-z0-9._-]{2,256}@[a-z]{2,64}$/, "A UPI ID such as name@bank");

/** LuckyMenu's own details on invoices, and the defaults for subscriptions. */
export const platformSettingsSchema = z.object({
	legalName: z.string(),
	address: z.string().nullable(),
	gstin: z.string().nullable(),
	upiId: z.string().nullable(),
	/** Where owners send a payment's reference (WhatsApp). */
	whatsapp: z.string().nullable(),
	invoicePrefix: z.string(),
	trialDays: z.number().int(),
	graceDays: z.number().int(),
	updatedAt: z.iso.datetime(),
});
export type PlatformSettings = z.infer<typeof platformSettingsSchema>;

/** Empty text clears an optional field. */
const cleared = <T extends z.ZodType>(schema: T) =>
	z.union([z.literal("").transform(() => null), schema.nullable()]);

/**
 * `admin.platformBilling.updateSettings`, the whole form. Without a GSTIN,
 * invoices carry no tax line. The trial days apply to restaurants created
 * afterwards, the grace days whenever a subscription becomes past due.
 */
export const updatePlatformSettingsInputSchema = z.object({
	legalName: z.string().trim().min(1).max(120),
	address: cleared(z.string().trim().max(300)),
	gstin: cleared(gstinSchema),
	upiId: cleared(upiIdSchema),
	/** As `phoneSchema` (tenancy.ts imports this file, so it is repeated here). */
	whatsapp: cleared(
		z
			.string()
			.trim()
			.regex(/^\+?[0-9][0-9 -]{5,18}[0-9]$/, "A phone number"),
	).optional(),
	invoicePrefix: z
		.string()
		.trim()
		.toUpperCase()
		.regex(/^[A-Z0-9]{1,8}$/, "1 to 8 letters or digits"),
	trialDays: z.number().int().min(1).max(365),
	graceDays: z.number().int().min(0).max(90),
});
export type UpdatePlatformSettingsInput = z.infer<typeof updatePlatformSettingsInputSchema>;
